// =============================================================================
// AI Visual Analyzer: upload photo to Storage, then call edge function with URL.
// No base64 in request body — avoids 502s from oversized payloads.
// =============================================================================

import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { prepareImageFileForUpload } from "./prepareImageForUpload";
import { sanitizeAiUserMessage } from "./aiUserFacingErrors";
import { logAiAdvisoryEvent } from "./aiAdvisory";

export type InventoryAnalysisUnit = "pcs" | "roll" | "bottle" | "box" | "set" | "other";
export type InventoryAnalysisCategory =
  | "appliance"
  | "device"
  | "consumable"
  | "cleaning"
  | "safety"
  | "maintenance"
  | "other";

/** Confidence 0–1 from API. */
export type ListingSuggestedDestination = "supply_closet" | "guest_amenities" | "unit_check" | "skip";

export interface AnalyzedInventoryItem {
  name: string;
  quantity: number;
  unit: InventoryAnalysisUnit;
  confidence: number;
  category?: InventoryAnalysisCategory;
  evidence?: string;
  needs_confirmation?: boolean;
  suggested_room?: string | null;
  /** Present when edge function is called with mode: "listing". */
  suggested_destination?: ListingSuggestedDestination;
  /** Specific cleaning instruction — only present for cleaning_task destination. */
  suggested_task?: string;
}

export interface PossibleMissingItem {
  name: string;
  reason: string;
  confidence: number;
}

export interface AnalyzeInventoryPhotoResult {
  room_guess: string;
  items: AnalyzedInventoryItem[];
  possible_missing_or_unclear?: PossibleMissingItem[];
  one_question_max?: string | null;
}

export interface AnalyzeInventoryPhotoOptions {
  roomHint?: string;
  /** Airbnb listing scan: classify items into inventory scopes vs unit check. */
  mode?: "listing";
}

/** Error from analyze-inventory-photo with server error_code and request_id. */
export class AnalyzePhotoError extends Error {
  constructor(
    message: string,
    public readonly error_code?: string,
    public readonly request_id?: string,
    public readonly status?: number
  ) {
    super(message);
    this.name = "AnalyzePhotoError";
  }
}

const STORAGE_BUCKET = "reference-photos";
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB client limit
const MAX_DIMENSION = 1600;
const TARGET_QUALITY = 0.8;

/** Resize/compress image for upload: max dimension 1600, quality 0.8, aim under ~1.5MB. */
export async function resizeImageForUpload(file: File): Promise<Blob> {
  const prepared = await prepareImageFileForUpload(file);
  if (!prepared.type.startsWith("image/")) {
    throw new Error("Please select an image file");
  }
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(prepared);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const scale = Math.min(1, MAX_DIMENSION / Math.max(w, h));
      const cw = Math.round(w * scale);
      const ch = Math.round(h * scale);
      const canvas = document.createElement("canvas");
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(prepared as Blob);
        return;
      }
      ctx.drawImage(img, 0, 0, cw, ch);
      const mime = prepared.type.startsWith("image/png") ? "image/png" : "image/jpeg";
      canvas.toBlob(
        (blob) => {
          resolve(blob || (prepared as Blob));
        },
        mime,
        TARGET_QUALITY
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(prepared as Blob);
    };
    img.src = url;
  });
}

/**
 * Upload a file to Storage at {propertyId}/inventory-scans/{yyyy-mm-dd}/{uuid}.{ext}.
 * Returns a signed URL (10 min) so the edge function can fetch the image even if the bucket is private.
 */
export async function uploadInventoryScanImage(
  file: File | Blob,
  propertyId: string
): Promise<{ imageUrl: string; path: string }> {
  const ext = file instanceof File && file.name ? (file.name.split(".").pop() || "jpg") : "jpg";
  const date = new Date().toISOString().slice(0, 10); // yyyy-mm-dd
  const uuid = crypto.randomUUID().replace(/-/g, "").slice(0, 12);
  const path = `${propertyId}/inventory-scans/${date}/${uuid}.${ext}`;
  const blob = file instanceof Blob ? file : (file as Blob);
  const { error } = await supabase.storage.from(STORAGE_BUCKET).upload(path, blob, {
    upsert: true,
    contentType: blob.type || "image/jpeg",
  });
  if (error) {
    throw new Error(error.message || "Upload failed");
  }
  // Use signed URL so the edge function can fetch the image (works for private buckets)
  const { data: signed, error: signErr } = await supabase.storage
    .from(STORAGE_BUCKET)
    .createSignedUrl(path, 60 * 10); // 10 minutes
  if (signErr || !signed?.signedUrl) {
    // Fallback to public URL if signed fails (e.g. bucket is public)
    const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
    return { imageUrl: data.publicUrl, path };
  }
  return { imageUrl: signed.signedUrl, path };
}

/**
 * Call edge function with an image URL (after upload). Used when modal has already uploaded.
 */
export async function analyzeByImageUrl(
  propertyId: string,
  imageUrl: string,
  imagePath: string,
  options: AnalyzeInventoryPhotoOptions = {}
): Promise<AnalyzeInventoryPhotoResult> {
  const body = {
    propertyId,
    room: options.roomHint ?? undefined,
    imageUrl,
    imagePath,
    mode: options.mode,
  };
  const t0 = performance.now();
  const { data, error } = await supabase.functions.invoke("analyze-inventory-photo", {
    body,
  });
  try {
    const parsed = await parseInvokeResponse(data, error);
    // Average confidence across items (best-effort) so we can audit reliability over time.
    const avg =
      parsed.items.length > 0
        ? parsed.items.reduce((s, it) => s + (typeof it.confidence === "number" ? it.confidence : 0), 0) /
          parsed.items.length
        : null;
    logAiAdvisoryEvent({
      kind: "inventory_photo_scan",
      propertyId,
      confidence: avg,
      durationMs: performance.now() - t0,
      outcome: "ok",
      extra: { itemCount: parsed.items.length, room: parsed.room_guess },
    });
    return parsed;
  } catch (err) {
    logAiAdvisoryEvent({
      kind: "inventory_photo_scan",
      propertyId,
      durationMs: performance.now() - t0,
      outcome: err instanceof AnalyzePhotoError ? (err.error_code ?? "error") : "error",
    });
    throw err;
  }
}

async function parseInvokeResponse(data: unknown, error: unknown): Promise<AnalyzeInventoryPhotoResult> {
  if (error) {
    let message = (error as Error).message || "Analysis failed";
    let error_code: string | undefined;
    let request_id: string | undefined;
    if (error instanceof FunctionsHttpError && (error as FunctionsHttpError).context) {
      try {
        const errBody = await ((error as FunctionsHttpError).context as Response).json() as {
          message?: string;
          error_code?: string;
          request_id?: string;
        };
        if (errBody && typeof errBody === "object") {
          message = errBody.message ?? message;
          error_code = errBody.error_code;
          request_id = errBody.request_id;
        }
      } catch {
        // keep message
      }
    }
    if (request_id) console.error("[analyze-inventory-photo] request_id:", request_id, message);
    throw new AnalyzePhotoError(sanitizeAiUserMessage(message, error_code), error_code, request_id);
  }
  const result = data as unknown;
  if (!result || typeof result !== "object") {
    throw new AnalyzePhotoError(sanitizeAiUserMessage("Invalid response from analyzer"));
  }
  const typed = result as { ok?: boolean; room_guess?: string; items?: unknown[] };
  if (typed.ok === true && Array.isArray(typed.items)) {
    return {
      room_guess: typed.room_guess ?? "other",
      items: typed.items as AnalyzeInventoryPhotoResult["items"],
      possible_missing_or_unclear: (typed as AnalyzeInventoryPhotoResult).possible_missing_or_unclear,
      one_question_max: (typed as AnalyzeInventoryPhotoResult).one_question_max,
    };
  }
  const errPayload = result as { message?: string; error_code?: string; request_id?: string };
  throw new AnalyzePhotoError(
    sanitizeAiUserMessage(errPayload.message ?? "Invalid response from analyzer", errPayload.error_code),
    errPayload.error_code,
    errPayload.request_id
  );
}

/**
 * Full flow: resize, upload to Storage, then call edge function with imageUrl.
 * Throws on upload or analysis failure.
 */
export async function analyzeInventoryPhoto(
  file: File,
  options: AnalyzeInventoryPhotoOptions & { propertyId: string }
): Promise<AnalyzeInventoryPhotoResult> {
  if (!file.type.startsWith("image/")) {
    throw new Error("Please select an image file");
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error("Image is too large. Use a smaller photo (under 10 MB).");
  }
  const resized = await resizeImageForUpload(file);
  const { imageUrl, path } = await uploadInventoryScanImage(resized, options.propertyId);
  return analyzeByImageUrl(options.propertyId, imageUrl, path, options);
}
